import React, { useState } from 'react';
import { requestAllPermissions, AllPermissions, arePermissionsGranted } from '../../utils/permissions';
import './welcome-message.scss';

interface WelcomeMessageProps {
  onComplete: (permissions: AllPermissions, voiceGender: 'male' | 'female') => void;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ onComplete }) => {
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<'male' | 'female'>('male'); // Default to male

  const handleContinue = async () => {
    setIsRequestingPermissions(true);
    setPermissionError(null);

    try {
      const permissions = await requestAllPermissions();
      
      // Check if we got the essential permissions
      if (!arePermissionsGranted(permissions)) {
        // Show error but still allow continuation with limited functionality
        const errors = [];
        if (!permissions.microphone.granted) errors.push('microphone');
        if (!permissions.camera.granted) errors.push('camera');
        if (!permissions.location.granted) errors.push('location');
        
        setPermissionError(`Some permissions were denied: ${errors.join(', ')}. You can still use the app with limited functionality.`);
      }
      
      // Complete after a short delay to show any error message
      setTimeout(() => {
        onComplete(permissions, selectedVoice);
      }, permissionError ? 2000 : 500);
      
    } catch (error) {
      setPermissionError('Failed to request permissions. You can still use the app with limited functionality.');
      setTimeout(() => {
        onComplete({
          location: { granted: false, error: 'Permission request failed' },
          microphone: { granted: false, error: 'Permission request failed' },
          camera: { granted: false, error: 'Permission request failed' }
        }, selectedVoice);
      }, 2000);
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div className="welcome-message">
      <div className="welcome-content">
        <div className="welcome-header">
          <h1>Welcome to Saaed Assistant!</h1>
          <p className="welcome-subtitle">
            Your intelligent traffic safety companion | مساعدك الذكي للسلامة المرورية
          </p>
        </div>

        {/* Voice Selection - First Thing User Sees */}
        <div className="voice-selection-section voice-selection-primary">
          <h3>Choose AI Voice | اختر صوت المساعد الذكي:</h3>
          <div className="voice-options">
            <button
              className={`voice-option ${selectedVoice === 'male' ? 'selected' : ''}`}
              onClick={() => setSelectedVoice('male')}
            >
              <span className="voice-icon">👨</span>
              <span className="voice-label">Male Voice | صوت رجالي</span>
            </button>
            <button
              className={`voice-option ${selectedVoice === 'female' ? 'selected' : ''}`}
              onClick={() => setSelectedVoice('female')}
            >
              <span className="voice-icon">👩</span>
              <span className="voice-label">Female Voice | صوت نسائي</span>
            </button>
          </div>
        </div>

        <div className="features-overview">
          <h2>What I can help you with: | كيف يمكنني مساعدتك؟</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-content">
                <h3>Accident Reporting | الإبلاغ عن الحوادث</h3>
                <p>Report and document traffic accidents with photos and videos | الإبلاغ عن الحوادث وتوثيقها بالصور والفيديو</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>24/7 Support | دعم على مدار الساعة</h3>
                <p>Get instant answers about SAAED services and traffic safety | احصل على إجابات فورية حول خدمات ساعد والسلامة المرورية</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>Voice Interaction | التفاعل الصوتي</h3>
                <p>Natural conversation in Arabic with your AI assistant | محادثة طبيعية بالعربية مع مساعدك الذكي</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>Emergency Support | الدعم الطارئ</h3>
                <p>Quick access to emergency services and patrol assistance | وصول سريع لخدمات الطوارئ ومساعدة الدوريات</p>
              </div>
            </div>
          </div>
        </div>

        <div className="permissions-section">
          <h3>To get started, we'll need access to: | للبدء، نحتاج إلى:</h3>
          <div className="permissions-list">
            <div className="permission-item">
              <div className="permission-details">
                <strong>Location | الموقع</strong>
                <span>For accident location reporting | للإبلاغ عن موقع الحادث</span>
              </div>
            </div>
            <div className="permission-item">
              <div className="permission-details">
                <strong>Microphone | الميكروفون</strong>
                <span>For voice interaction | للتفاعل الصوتي</span>
              </div>
            </div>
            <div className="permission-item">
              <div className="permission-details">
                <strong>Camera | الكاميرا</strong>
                <span>For accident photo documentation | لتوثيق صور الحادث</span>
              </div>
            </div>
          </div>
        </div>

        {showDetails && (
          <div className="privacy-details">
            <h4>Privacy & Security | الخصوصية والأمان</h4>
            <ul>
              <li>Your location is only used for accident reporting and is not stored | يُستخدم موقعك فقط للإبلاغ عن الحوادث ولا يتم تخزينه</li>
              <li>Camera feed is processed locally and not recorded | يتم معالجة بث الكاميرا محلياً ولا يتم تسجيله</li>
              <li>Voice data is processed securely and not saved | تتم معالجة بيانات الصوت بشكل آمن ولا يتم حفظها</li>
              <li>All data transmission is encrypted | جميع عمليات نقل البيانات مشفرة</li>
            </ul>
          </div>
        )}

        {permissionError && (
          <div className="permission-error">
            <p>{permissionError}</p>
          </div>
        )}

        <div className="welcome-actions">
          <button 
            className="btn-primary"
            onClick={handleContinue}
            disabled={isRequestingPermissions}
          >
            {isRequestingPermissions ? 'Requesting Permissions...' : 'Continue'}
          </button>
          
          <button 
            className="btn-secondary"
            onClick={toggleDetails}
          >
            {showDetails ? 'Hide Details' : 'Privacy Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessage;
